import {
  CalendarIcon,
  ExclamationCircleIcon,
  PencilIcon,
} from '@heroicons/react/outline';
import WalletEuroIcon from 'assets/svg/icons/wallet-euro-icon.svg?react';
import WalletPercentageIcon from 'assets/svg/icons/wallet-percentage.svg?react';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import { renderCustomSubAccountIcon } from 'components/atoms/Icons/SpaceIcons';
import { Modal } from 'components/atoms/Modal';
import FileSearchIcon from 'components/atoms/SVG/FileSearchIcon.component';
import RepeatIcon from 'components/atoms/SVG/RepeatIcon.component';
import ModalHeading from 'components/molecules/ModalHeading';
import {
  isCustomSubAccount,
  isTaxReserveAccountableBank,
  isMainAccountableBank,
  TransferFieldNames,
} from 'components/pages/Banks/Wallet/helpers';
import TransferScheduleForm from 'components/pages/Banks/Wallet/TransferScheduleForm.component';
import dayjs from 'dayjs';
import { motion } from 'motion/react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CurrenciesEnum } from 'types/global.types';
import {
  StandingOrderFrequency,
  type Connector,
} from 'types/transactions.types';
import {
  WEBAPP_BANKING_BANKLIST_REVIEWSCHEDULEDPAYMENTS_CLICKEDEDITSCHEDULE,
  WEBAPP_BANKING_PAYMENTTRANSFER_EXTERNALTRANSFER_CLICKEDCANCEL,
  WEBAPP_BANKING_PAYMENTTRANSFER_EXTERNALTRANSFER_CLICKEDCONFIRMTRANSFER,
} from 'utils/amplitude/events/bank.amplitude';
import { fadeTransition } from 'utils/constants';
import formatMoney from 'utils/formatMoney';
import SimilarTransactionAlert from '../../SimilarTransactionAlert.component';
import type { TransferSchedule } from '../types';

export interface TransferReviewData {
  // Source account
  sourceAccount?: Connector;

  // Destination (internal or external)
  destinationAccount?: Connector;
  // External transfer data
  recipientName?: string;
  recipientIBAN?: string;

  // Transfer details
  amount: number;
  communication?: string;
  communicationType?: string;
  instantTransfer?: boolean;

  // Scheduling data
  period?: string;
  firstExecutionDate?: Date;
  lastExecutionDate?: Date;
  requestedExecutionAt?: Date;

  // Transfer type
  isInternal?: boolean;
  isStandingOrder?: boolean;
}

export interface TransferReviewStepProps {
  data: TransferReviewData;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  onScheduleChange?: (scheduleData: TransferSchedule) => void;
  sourceAccount?: Connector;
}

const TransferReviewStep: FC<TransferReviewStepProps> = ({
  data,
  isLoading,
  onConfirm,
  onCancel,
  onScheduleChange,
  sourceAccount,
}) => {
  const { t } = useTranslation();

  const getSourceAccountIcon = (account?: Connector) => {
    if (!account) return <WalletEuroIcon className="w-6 h-6 text-bank-700" />;
    if (isCustomSubAccount(account) && account.icon) {
      return renderCustomSubAccountIcon(account.icon, 'w-6 h-6');
    }
    if (isTaxReserveAccountableBank(account)) {
      return <WalletPercentageIcon className="w-6 h-6 text-taxes-600" />;
    }
    return <WalletEuroIcon className="w-6 h-6 text-taxes-600" />;
  };

  const getDestinationIcon = (account?: Connector) => {
    if (!account) {
      // External transfer - show initials
      return (
        <p className="font-semibold capitalize text-primary">
          {data.recipientName?.slice(0, 2) || 'EX'}
        </p>
      );
    }

    if (isCustomSubAccount(account) && account.icon) {
      return renderCustomSubAccountIcon(account.icon, 'w-6 h-6');
    }
    if (isTaxReserveAccountableBank(account)) {
      return <WalletPercentageIcon className="w-6 h-6 text-taxes-600" />;
    }
    return <WalletEuroIcon className="w-6 h-6 text-taxes-600" />;
  };

  const getDestinationTitle = () => {
    if (data.isInternal && data.destinationAccount) {
      return data.destinationAccount.title;
    }
    return data.recipientName || 'External Account';
  };

  const getSimilarTransactionProps = () => {
    if (data.isInternal) {
      return {
        fromIBAN: data.sourceAccount?.bankAccountReference?.IBAN || '',
        toIBAN: data.destinationAccount?.bankAccountReference?.IBAN || '',
      };
    }
    return {
      fromIBAN: data.sourceAccount?.bankAccountReference?.IBAN || '',
      toIBAN: data.recipientIBAN || '',
    };
  };

  const sourceIsCustomSubAccount =
    data?.sourceAccount && isCustomSubAccount(data.sourceAccount);
  const destinationIsCustomSubAccount =
    data?.destinationAccount && isCustomSubAccount(data.destinationAccount);
  const isScheduledPayment =
    (data.period && data.firstExecutionDate) || data.requestedExecutionAt;
  const frequencyValue = data.period || StandingOrderFrequency.OneTime;

  const handleScheduleSubmit = (formData: Record<string, any>) => {
    // Convert form field names to TransferSchedule format
    const scheduleData: TransferSchedule = {
      period: formData[TransferFieldNames.period] as StandingOrderFrequency,
      firstExecutionDate: formData[TransferFieldNames.firstExecutionDate],
      lastExecutionDate: formData[TransferFieldNames.lastExecutionDate],
    };
    onScheduleChange?.(scheduleData);
  };

  const canEditSchedule =
    onScheduleChange &&
    (!data.isInternal || isMainAccountableBank(data.sourceAccount)) &&
    !data.instantTransfer;

  const renderScheduleContent = () => (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center">
          <CalendarIcon className="w-6 h-6 text-primary" />
        </div>

        <div className="text-left">
          <h2 className="font-semibold text-primary flex items-center space-x-2">
            <span>
              {t(
                `accountable_banking.scheduled_payments.review.frequency.${frequencyValue}`,
              )}
            </span>
            <RepeatIcon className="w-5 h-5 text-primary-300" />
          </h2>

          <p className="text-sm text-primary-300">
            {t('accountable_banking.scheduled_payments.review.next_date', {
              nextDate: dayjs(data.firstExecutionDate).format('DD MMMM YYYY'),
            })}
          </p>

          {data.lastExecutionDate && (
            <p className="text-sm text-primary-300">
              {t('accountable_banking.scheduled_payments.review.last_date', {
                lastDate: dayjs(data.lastExecutionDate).format('DD MMMM YYYY'),
              })}
            </p>
          )}
        </div>
      </div>

      {canEditSchedule && <PencilIcon className="w-5 h-5 text-primary" />}
    </div>
  );

  return (
    <motion.div
      {...fadeTransition}
      className="w-full px-6 py-4 bg-white rounded-lg"
    >
      <ModalHeading
        title={t('accountable_banking.review_transfer.title')}
        subtitle={t('accountable_banking.review_transfer.subtitle')}
        Icon={FileSearchIcon}
      />

      <motion.div {...fadeTransition} className="my-4 text-center w-full">
        <motion.div
          {...fadeTransition}
          className="w-full my-6 border border-taxes-100 rounded-lg"
        >
          {/* Source Account */}
          <div className="p-6 py-3 flex items-center justify-between hover:bg-taxes-50 border-b border-b-taxes-100 last-of-type:!border-none">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center space-x-3">
                <div
                  className={classNames(
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    {
                      'bg-bank-100': sourceIsCustomSubAccount,
                      'bg-taxes-100': !sourceIsCustomSubAccount,
                    },
                  )}
                >
                  {getSourceAccountIcon(data.sourceAccount)}
                </div>

                <div className="text-left">
                  <span className="text-sm text-primary-300">
                    {t('accountable_banking.review_transfer.from')}
                  </span>
                  <h2 className="font-semibold text-primary">
                    {data.sourceAccount?.title || 'Unknown Account'}
                  </h2>
                </div>
              </div>

              <span className="text-primary font-semibold">
                {formatMoney(data.amount)}
              </span>
            </div>
          </div>

          {/* Destination Account */}
          <div className="p-6 py-3 flex items-center justify-between hover:bg-taxes-50 border-b border-b-taxes-100 last-of-type:!border-none">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center space-x-3">
                <div
                  className={classNames(
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    {
                      'bg-bank-100': destinationIsCustomSubAccount,
                      'bg-taxes-100': !destinationIsCustomSubAccount,
                    },
                  )}
                >
                  {getDestinationIcon(data.destinationAccount)}
                </div>

                <div className="text-left">
                  <span className="text-sm text-primary-300">
                    {t('accountable_banking.review_transfer.to')}
                  </span>
                  <h2 className="font-semibold text-primary">
                    {getDestinationTitle()}
                  </h2>

                  {data.recipientIBAN && (
                    <p className="text-sm text-primary-300">
                      {data.recipientIBAN}
                    </p>
                  )}

                  {data.communication && (
                    <p className="text-sm text-primary-300 mt-1">
                      <span className="font-semibold">
                        {t(
                          'accountable_banking.external_transfer.fields.communication.label',
                        )}
                        :
                      </span>{' '}
                      {data.communication}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Schedule Information (for standing orders) */}
          {isScheduledPayment &&
            (canEditSchedule ? (
              <Modal
                name="edit-schedule"
                trigger={({ openModal }) => (
                  <div
                    className="p-6 py-3 flex items-center justify-between hover:bg-taxes-50 border-b border-b-taxes-100 last-of-type:!border-none cursor-pointer"
                    onClick={openModal}
                  >
                    {renderScheduleContent()}
                  </div>
                )}
                render={({ closeModal }) => (
                  <TransferScheduleForm
                    sourceAccount={sourceAccount || data?.sourceAccount}
                    isInternalTransfer={data.isInternal}
                    defaultValues={{
                      [TransferFieldNames.period]: data.period as any,
                      [TransferFieldNames.firstExecutionDate]:
                        data.firstExecutionDate || new Date(),
                      [TransferFieldNames.lastExecutionDate]:
                        data.lastExecutionDate,
                    }}
                    onSubmit={(formData) => {
                      handleScheduleSubmit(formData);
                      closeModal();
                    }}
                  />
                )}
              />
            ) : (
              <div className="p-6 py-3 flex items-center justify-between hover:bg-taxes-50 border-b border-b-taxes-100 last-of-type:!border-none">
                {renderScheduleContent()}
              </div>
            ))}
        </motion.div>

        {/* Similar Transaction Alert */}
        <SimilarTransactionAlert
          amount={data.amount}
          currency={CurrenciesEnum.EUR}
          {...getSimilarTransactionProps()}
          onConfirm={onConfirm}
          isLoading={isLoading || false}
        />

        {/* Disclaimer for immediate external instant transfers only */}
        {data.isInternal || data.instantTransfer ? null : (
          <div className="flex space-x-4 mt-3">
            <ExclamationCircleIcon className="w-5 h-5 text-primary-700 mt-2" />
            <p className="text-primary-700 text-left">
              {t('accountable_banking.external_transfer.delay_disclaimer')}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="w-full flex space-x-4 mt-10">
          <Button
            tracingEvent={
              WEBAPP_BANKING_PAYMENTTRANSFER_EXTERNALTRANSFER_CLICKEDCANCEL
            }
            structure="secondary"
            onClick={onCancel}
            className="flex-1 capitalize"
          >
            {t('cancel')}
          </Button>

          <Button
            type="submit"
            tracingEvent={
              WEBAPP_BANKING_PAYMENTTRANSFER_EXTERNALTRANSFER_CLICKEDCONFIRMTRANSFER
            }
            onClick={onConfirm}
            className="flex-1"
            loading={isLoading}
          >
            {t('confirm')}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TransferReviewStep;
